import React, { useRef } from "react";
import { connect } from "react-redux";
import { CustomerSurveyTypeFormDto, FormOptionDto } from "interfaces/Mueller.Business.Models";
import MCard, { MCardControlMethods } from "components/Shared/MCard.tsx";
import { Button } from "react-md";
import { 
  DATA_TYPE_DROPDOWN2, 
  DATA_TYPE_STRING, 
  DATA_TYPE_NUMBER_STRING, 
  DATA_TYPE_BOOLEAN,
  DATA_TYPE_MONEY,
} from "typeConstants/dataTypes";
import * as yup from "yup";
import { calcToolOptions } from "./CustomerSurveyTypeForms";

interface AddEditCustomerSurveyTypeFormsProps {
  form: CustomerSurveyTypeFormDto | null; // Pass null to add new form
  onCancel: () => void;
  onSave: (form: CustomerSurveyTypeFormDto) => void;
  // Redux
  formOptions: FormOptionDto[],
}

const AddEditCustomerSurveyTypeForms = (props: AddEditCustomerSurveyTypeFormsProps) => {
  const cardControlMethods = useRef<MCardControlMethods>();
  
  const handleSave = () => {
    if (!cardControlMethods.current.runValidation(true)) {
      return;
    }
    const cardData = cardControlMethods.current.getEditedDataObject();
    let formData = null;
    if (props.form) {
      formData = { ...props.form, ...cardData };
    } else {
      formData = {
        name: "", // This is only used on the front end for display purposes
        formCode: cardData.formCode ?? props.form.formCode,
        displayOrder: parseInt(cardData.displayOrder),
        isSupplement: !!cardData.isSupplement,
        customerFee: cardData.customerFee? parseFloat(cardData.customerFee) : null,
        isOnSite: !!cardData.isOnSite,
        isCallCenter: !!cardData.isCallCenter,
        isConsumerWeb: !!cardData.isConsumerWeb,
        calcToolCode: cardData.calcToolCode,
        isFieldSupplement: !!cardData.isFieldSupplement,
        isHiddenFromFR: !!cardData.isHiddenFromFR,
        isHiddenFromQA: !!cardData.isHiddenFromQA,
        isHiddentFromCustomer: !!cardData.isHiddentFromCustomer,
        isHiddenFromAppraiser: !!cardData.isHiddenFromAppraiser,
        isHiddenFromCER: !!cardData.isHiddenFromCER,
      };
    }
    props.onSave(formData);
  };

  const cardFields = [
    {
      key: "formCode",
      label: "Form",
      validation:  props.form ? null : yup.string().required("Form is required"),
      value: props.form ? props.form.name : null,
      dataType: props.form ? DATA_TYPE_STRING : DATA_TYPE_DROPDOWN2,
      editable: props.form ? false : true,
      dataTypeOptions: props.form 
        ? null 
        : { options: props.formOptions.map(f => ({ label: f.name, value: f.formCode })), nullable: false }
    },
    {
      key: "displayOrder",
      dataType: DATA_TYPE_NUMBER_STRING,
      label: "Display Order",
      value: props.form?.displayOrder,
      validation: yup.string().nullable().required("Display order is required"),
    },
    {
      key: "isSupplement",
      dataType: DATA_TYPE_BOOLEAN,
      label: "Supplement Can Be Added by Customer",
      value: props.form?.isSupplement,
    },
    {
      key: "customerFee",
      dataType: DATA_TYPE_MONEY,
      label: "Customer Fee (optional)",
      value: props.form?.customerFee,
    },
    {
      key: "isOnSite",
      dataType: DATA_TYPE_BOOLEAN,
      label: "On Site Form",
      value: props.form?.isOnSite,
    },
    {
      key: "isCallCenter",
      dataType: DATA_TYPE_BOOLEAN,
      label: "Call Center Form",
      value: props.form?.isCallCenter,
    },
    {
      key: "isConsumerWeb",
      dataType: DATA_TYPE_BOOLEAN,
      label: "Consumer Web Form",
      value: props.form?.isConsumerWeb,
    },
    {
      key: "calcToolCode",
      label: "Calc Tool (optional)",
      value: props.form?.calcToolCode,
      dataType: DATA_TYPE_DROPDOWN2,
      dataTypeOptions: { options: calcToolOptions, nullable: true }
    },
    {
      key: "isFieldSupplement",
      dataType: DATA_TYPE_BOOLEAN,
      label: "Supplement Can Be Added by FR",
      value: props.form?.isFieldSupplement,
    },
    {
      key: "isHiddenFromFR",
      dataType: DATA_TYPE_BOOLEAN,
      label: "Hide from FR",
      value: props.form?.isHiddenFromFR,
    },
    {
      key: "isHiddenFromQA",
      dataType: DATA_TYPE_BOOLEAN,
      label: "Hide from QA",
      value: props.form?.isHiddenFromQA,
    },
    {
      key: "isHiddentFromCustomer",
      dataType: DATA_TYPE_BOOLEAN,
      label: "Hide from Customer",
      value: props.form?.isHiddentFromCustomer,
    },
    {
      key: "isHiddenFromAppraiser",
      dataType: DATA_TYPE_BOOLEAN,
      label: "Hide from Appraiser",
      value: props.form?.isHiddenFromAppraiser,
    },
    {
      key: "isHiddenFromCER",
      dataType: DATA_TYPE_BOOLEAN,
      label: "Hide from CER",
      value: props.form?.isHiddenFromCER,
    },
  ];

  return (
    <MCard
      key="survey-type-form-edit"
      className="survey-type-form-edit"
      title="Form Details"
      onlyEditable
      fields={cardFields}
      getControlMethods={controlMethods => cardControlMethods.current = controlMethods}
    >
      <div className="survey-type-form-edit-button-container">
        <Button primary raised onClick={handleSave}>Save</Button>
        <Button primary raised onClick={props.onCancel}>Cancel</Button>
      </div>
    </MCard>
  );
};

const mapStateToProps = state => ({
  formOptions: state.customer.customerFormOptions,
});

export default connect(mapStateToProps, null)(AddEditCustomerSurveyTypeForms);